/**
 * Agent System - Like Claude Code
 * Implements GSD, DevTeam, and specialized agents
 */

export const AGENT_TYPES = {
  // GSD (Get Stuff Done) Agents
  GSD_ROADMAPPER: {
    id: 'gsd-roadmapper',
    name: 'GSD Roadmapper',
    description: 'Creates project roadmaps with phase breakdown and success criteria',
    capabilities: ['planning', 'architecture', 'requirements'],
    systemPrompt: `You are a GSD Roadmapper agent. Break projects into phases with clear goals and success criteria.`
  },

  GSD_PLANNER: {
    id: 'gsd-planner',
    name: 'GSD Planner',
    description: 'Creates detailed phase plans with task breakdown and dependencies',
    capabilities: ['planning', 'task-breakdown'],
    systemPrompt: `You are a GSD Planner. Create executable phase plans with dependencies.`
  },

  GSD_EXECUTOR: {
    id: 'gsd-executor',
    name: 'GSD Executor',
    description: 'Executes plans with atomic commits and state management',
    capabilities: ['execution', 'git', 'file-operations'],
    systemPrompt: `You are a GSD Executor. Execute plans step-by-step with atomic commits.`
  },

  GSD_VERIFIER: {
    id: 'gsd-verifier',
    name: 'GSD Verifier',
    description: 'Verifies phase goals through goal-backward analysis',
    capabilities: ['verification', 'testing'],
    systemPrompt: `You are a GSD Verifier. Verify implementations meet phase goals.`
  },

  GSD_DEBUGGER: {
    id: 'gsd-debugger',
    name: 'GSD Debugger',
    description: 'Investigates bugs using scientific method',
    capabilities: ['debugging', 'investigation'],
    systemPrompt: `You are a GSD Debugger. Use scientific method to find and fix bugs.`
  },

  // DevTeam Agents
  DEVTEAM_FRONTEND: {
    id: 'devteam-frontend',
    name: 'Frontend Developer',
    description: 'UI/UX implementation - React, Angular, Vue, styling',
    capabilities: ['frontend', 'ui', 'react', 'css'],
    systemPrompt: `You are a Frontend Developer. Expert in React, Vue, Angular, TypeScript, and CSS.

Available tools:
- File operations (read, write, edit)
- Git operations
- Web search for documentation
- Code execution for testing

Write clean, accessible, performant UI code following best practices.`
  },

  DEVTEAM_BACKEND: {
    id: 'devteam-backend',
    name: 'Backend Developer',
    description: 'APIs, services, databases, authentication',
    capabilities: ['backend', 'api', 'database', 'auth'],
    systemPrompt: `You are a Backend Developer. Expert in Node.js, Python, Go, databases, and APIs.

Available tools:
- File operations
- Database operations
- Git operations
- Web search
- Code execution

Write secure, scalable backend code with proper error handling.`
  },

  DEVTEAM_ARCHITECT: {
    id: 'devteam-architect',
    name: 'System Architect',
    description: 'Architecture design, planning, tech decisions',
    capabilities: ['architecture', 'design', 'planning'],
    systemPrompt: `You are a System Architect. Design scalable, maintainable architectures.

Create detailed implementation plans including:
- File structure
- Component boundaries
- Data flows
- API contracts
- Testing strategies

Follow existing codebase patterns.`
  },

  DEVTEAM_QA: {
    id: 'devteam-qa',
    name: 'QA Engineer',
    description: 'Testing, test automation, quality assurance',
    capabilities: ['testing', 'qa', 'automation'],
    systemPrompt: `You are a QA Engineer. Create comprehensive tests with high coverage.

Write tests for:
- Unit tests (Jest, Pytest)
- Integration tests
- E2E tests (Cypress, Playwright)
- Accessibility tests

Ensure code quality and catch bugs early.`
  },

  DEVTEAM_REVIEWER: {
    id: 'devteam-reviewer',
    name: 'Code Reviewer',
    description: 'Code review, security audit, quality check',
    capabilities: ['review', 'security', 'quality'],
    systemPrompt: `You are a Code Reviewer. Review code for:
- Correctness
- Security (OWASP Top 10)
- Performance
- Code style
- Test coverage
- Accessibility

Provide actionable feedback with severity ratings.`
  },

  DEVTEAM_DOCS: {
    id: 'devteam-docs',
    name: 'Documentation Specialist',
    description: 'Technical writing, documentation, guides',
    capabilities: ['documentation', 'writing'],
    systemPrompt: `You are a Documentation Specialist. Write clear, comprehensive documentation.

Create:
- README files
- API documentation
- Technical guides
- Changelogs
- Architecture Decision Records (ADRs)

Make documentation accessible to developers of all levels.`
  },

  // Specialized Agents
  EXPLORE: {
    id: 'explore',
    name: 'Explore Agent',
    description: 'Fast read-only search for locating code',
    capabilities: ['search', 'grep', 'find'],
    systemPrompt: `You are an Explore agent. Quickly find files and code patterns.

Use:
- grep for symbols/keywords
- find for file patterns
- Read excerpts for context

Fast, focused searches only.`
  },

  ULTRATHINK: {
    id: 'ultrathink-strategist',
    name: 'Strategic Thinker',
    description: 'Deep multi-dimensional analysis and strategic thinking',
    capabilities: ['strategy', 'analysis', 'reasoning'],
    systemPrompt: `You are an Ultra-Think Strategist. Provide deep, multi-dimensional analysis.

For complex problems:
- Analyze from multiple angles
- Consider trade-offs
- Provide strategic recommendations
- Think long-term implications

Use advanced reasoning for breakthrough insights.`
  }
};

/**
 * Agent Manager
 */
export class AgentManager {
  constructor(modelClient, toolExecutor) {
    this.modelClient = modelClient;
    this.toolExecutor = toolExecutor;
    this.activeAgents = new Map();
  }

  /**
   * Create and spawn an agent
   */
  async spawnAgent(agentType, task, options = {}) {
    const agentConfig = AGENT_TYPES[agentType];
    if (!agentConfig) {
      throw new Error(`Unknown agent type: ${agentType}`);
    }

    const agentId = `${agentConfig.id}-${Date.now()}`;

    const agent = {
      id: agentId,
      type: agentType,
      config: agentConfig,
      task,
      status: 'running',
      messages: [],
      toolCalls: [],
      startTime: Date.now(),
      ...options
    };

    this.activeAgents.set(agentId, agent);

    console.log(`[Agent] Spawned ${agentConfig.name} for: ${task}`);

    return agentId;
  }

  /**
   * Run agent with tool use
   */
  async runAgent(agentId, userMessage, options = {}) {
    const agent = this.activeAgents.get(agentId);
    if (!agent) {
      throw new Error(`Agent ${agentId} not found`);
    }

    const {
      maxTurns = 10,
      temperature = 0.7,
      enableTools = true,
      onToolCall = null,
      onThinking = null
    } = options;

    // Build messages with system prompt
    const messages = [
      {
        role: 'system',
        content: agent.config.systemPrompt
      },
      ...agent.messages,
      {
        role: 'user',
        content: userMessage
      }
    ];

    let turn = 0;
    let finalResponse = null;

    while (turn < maxTurns && !finalResponse) {
      turn++;

      if (onThinking) {
        onThinking(`${agent.config.name}: Turn ${turn}`);
      }

      // Call model
      const response = await this.modelClient.runInference(
        options.model || 'deepseek-v4-pro:cloud',
        messages,
        { temperature }
      );

      const responseText = response.content || '';

      // Check for tool use
      if (enableTools && this.hasToolCalls(responseText)) {
        const toolCalls = this.extractToolCalls(responseText);

        // Execute tools
        const toolResults = [];
        for (const toolCall of toolCalls) {
          if (onToolCall) {
            onToolCall(toolCall.tool, toolCall.parameters);
          }

          const result = await this.toolExecutor.executeTool(
            toolCall.tool,
            toolCall.parameters
          );

          toolResults.push({
            tool: toolCall.tool,
            parameters: toolCall.parameters,
            result
          });

          agent.toolCalls.push({
            tool: toolCall.tool,
            parameters: toolCall.parameters,
            result,
            timestamp: Date.now()
          });
        }

        // Add tool results to conversation
        messages.push({
          role: 'assistant',
          content: responseText
        });

        messages.push({
          role: 'user',
          content: `Tool results:\n${JSON.stringify(toolResults, null, 2)}`
        });

        continue;
      }

      // No tool calls - final response
      finalResponse = {
        content: responseText,
        agent: agent.config.name,
        turns: turn,
        toolCalls: agent.toolCalls.length
      };

      agent.messages.push({
        role: 'user',
        content: userMessage
      });

      agent.messages.push({
        role: 'assistant',
        content: responseText
      });
    }

    agent.status = 'completed';
    agent.endTime = Date.now();
    agent.duration = agent.endTime - agent.startTime;

    return finalResponse;
  }

  /**
   * Check if response contains tool calls
   */
  hasToolCalls(text) {
    return text.includes('<tool_use>') ||
           text.includes('TOOL_CALL:') ||
           text.includes('USE_TOOL:');
  }

  /**
   * Extract tool calls from response
   */
  extractToolCalls(text) {
    const toolCalls = [];

    // Try Anthropic format
    const toolUseRegex = /<tool_use>(.*?)<\/tool_use>/gs;
    const matches = text.matchAll(toolUseRegex);

    for (const match of matches) {
      try {
        const toolData = JSON.parse(match[1]);
        toolCalls.push({
          tool: toolData.name,
          parameters: toolData.parameters || {}
        });
      } catch (e) {
        console.warn('Failed to parse tool use:', e);
      }
    }

    // Try simple format
    if (toolCalls.length === 0) {
      const lines = text.split('\n');
      let currentTool = null;
      let paramsText = '';

      for (const line of lines) {
        if (line.startsWith('TOOL_CALL:') || line.startsWith('USE_TOOL:')) {
          if (currentTool) {
            try {
              toolCalls.push({
                tool: currentTool,
                parameters: paramsText ? JSON.parse(paramsText) : {}
              });
            } catch (e) {}
          }

          currentTool = line.split(':')[1].trim();
          paramsText = '';
        } else if (line.startsWith('PARAMETERS:')) {
          paramsText = line.replace('PARAMETERS:', '').trim();
        }
      }

      if (currentTool) {
        try {
          toolCalls.push({
            tool: currentTool,
            parameters: paramsText ? JSON.parse(paramsText) : {}
          });
        } catch (e) {}
      }
    }

    return toolCalls;
  }

  /**
   * Get agent status
   */
  getAgentStatus(agentId) {
    return this.activeAgents.get(agentId);
  }

  /**
   * List all active agents
   */
  listAgents() {
    return Array.from(this.activeAgents.values());
  }

  /**
   * Terminate agent
   */
  terminateAgent(agentId) {
    const agent = this.activeAgents.get(agentId);
    if (agent) {
      agent.status = 'terminated';
      agent.endTime = Date.now();
      return true;
    }
    return false;
  }
}

/**
 * Detect which agent to use based on task
 */
export function detectAgentType(task) {
  const taskLower = task.toLowerCase();

  // Frontend patterns
  if (taskLower.match(/\b(ui|component|style|css|react|vue|angular|frontend)\b/)) {
    return 'DEVTEAM_FRONTEND';
  }

  // Backend patterns
  if (taskLower.match(/\b(api|endpoint|database|server|backend|auth)\b/)) {
    return 'DEVTEAM_BACKEND';
  }

  // Testing patterns
  if (taskLower.match(/\b(test|testing|qa|coverage|cypress|playwright)\b/)) {
    return 'DEVTEAM_QA';
  }

  // Review patterns
  if (taskLower.match(/\b(review|audit|security|quality)\b/)) {
    return 'DEVTEAM_REVIEWER';
  }

  // Planning patterns
  if (taskLower.match(/\b(plan|design|architecture|roadmap)\b/)) {
    return 'DEVTEAM_ARCHITECT';
  }

  // Debug patterns
  if (taskLower.match(/\b(debug|fix|bug|investigate|troubleshoot)\b/)) {
    return 'GSD_DEBUGGER';
  }

  // Documentation patterns
  if (taskLower.match(/\b(document|readme|docs|guide)\b/)) {
    return 'DEVTEAM_DOCS';
  }

  // Search patterns
  if (taskLower.match(/\b(find|search|locate|where)\b/)) {
    return 'EXPLORE';
  }

  // Default to architect for planning
  return 'DEVTEAM_ARCHITECT';
}
